package org.zest.insan_360_website.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.storage.getItem
import com.varabyte.kobweb.browser.storage.setItem
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.localStorage
import kotlinx.browser.sessionStorage
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.zest.insan_360_website.components.layouts.containerShadowStyle
import org.zest.insan_360_website.components.layouts.userPageBorderStrongColor
import org.zest.insan_360_website.components.layouts.userPageColor
import org.zest.insan_360_website.components.widgets.LocalModal
import org.zest.insan_360_website.components.widgets.LocalToast
import org.zest.insan_360_website.components.widgets.ToastType
import org.zest.insan_360_website.components.widgets.VideoModal
import org.zest.insan_360_website.components.widgets.processData
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.AssessmentPageState
import org.zest.insan_360_website.data.page_states.AssessmentPageState.submitTest
import org.zest.insan_360_website.data.page_states.CreditState.fetchCurrentCreditStatus
import org.zest.insan_360_website.data.page_states.ResultPageState.hasFailed
import org.zest.insan_360_website.data.page_states.ResultPageState.hasSubmitted
import org.zest.insan_360_website.data.page_states.ResultPageState.submissionTrial
import org.zest.insan_360_website.data.page_states.AssessmentPageState.SubmitTestResult
import kotlin.time.Duration.Companion.seconds

val resultWordLibrary = LanguageSettings.ResultPage

@OptIn(DelicateApi::class)
@Page
@Layout(".components.layouts.ResultPageLayout")
@Composable
fun ResultPage(ctx: PageContext) {
    val toast = LocalToast.current
    val answers = localStorage.getItem(USER_ANSWERS_KEY) ?: List(24) { Answer(null, null) }
    val modal = LocalModal.current
    var showVideoModal by remember { mutableStateOf(true) }

    val (plotPointsMore, plotPointsLess, plotPointsDifference) = processData(answers)

    LaunchedEffect(showVideoModal, answers) {
        if (showVideoModal && answers.all { it.isAnswered() }) {
            modal.show(closeableOutside = false) {
                VideoModal(
                    videoUrls = listOf("https://vz-65b7a693-e53.b-cdn.net/fc6633cd-4481-45d8-bbd1-328575d21d61/play_720p.mp4"), // Placeholder for result video
                    title = LanguageSettings.ResultPage.video_modal_title.localized,
                    finishButtonText = LanguageSettings.ResultPage.video_modal_finish.localized,
                    proceedButtonText = LanguageSettings.ResultPage.video_modal_view_results.localized,
                    onProceed = {
                        modal.hide()
                        showVideoModal = false
                    },
                    onCancel = null // Forced to watch
                )
            }
        }
    }

    LaunchedEffect(submissionTrial, hasFailed) {
        if (!hasFailed && answers.all { it.isAnswered() }) {
            while (!hasSubmitted && submissionTrial < 3) {
                if (submissionTrial > 0) {
                    toast.show(resultWordLibrary.retrying.localized, ToastType.INFO)
                } else {
                    toast.show(resultWordLibrary.submitting.localized, ToastType.INFO)
                }

                val result = submitTest(toast, plotPointsMore, plotPointsLess, plotPointsDifference) {
                    fetchCurrentCreditStatus()
                    hasSubmitted = true
                    AssessmentPageState.reset()
                    sessionStorage.setItem(USER_QUESTION_INDEX_KEY, 0)
                }

                when (result) {
                    SubmitTestResult.SUCCESS -> break
                    SubmitTestResult.AUTH_EXPIRED -> {
                        toast.show(LanguageSettings.ResponseError.session_expired.localized, ToastType.ERROR)
                        hasFailed = true
                        break
                    }
                    SubmitTestResult.FATAL_ERROR -> {
                        hasFailed = true
                        break
                    }
                    SubmitTestResult.NETWORK_ERROR -> {
                        delay(3.seconds)
                        if (!hasSubmitted) {
                            submissionTrial++
                        }
                    }
                }
            }
            if (!hasSubmitted && submissionTrial >= 3) {
                toast.show(resultWordLibrary.submit_failed_toast.localized, ToastType.ERROR)
                hasFailed = true
            }
        }
    }

    if (answers.all { it.isAnswered() } || hasSubmitted) {
        Box(
            Modifier
                .backgroundColor(userPageColor)
                .border {
                    width(1.px)
                    color(userPageBorderStrongColor)
                    style(org.jetbrains.compose.web.css.LineStyle.Solid)
                }
                .borderRadius(1.cssRem)
                .padding(2.cssRem)
                .then(containerShadowStyle.toModifier())
                .fontSize(1.5.cssRem)
                .maxWidth(80.percent)
                .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center),
            contentAlignment = Alignment.Center
        ) {
            Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.gap(1.cssRem)) {
                if (hasSubmitted) {
                    SpanText(resultWordLibrary.thank_you.localized)
                } else if (hasFailed) {
                    SpanText(resultWordLibrary.error_submitting.localized)
                    Button(onClick = { 
                        submissionTrial = 0 
                        hasFailed = false
                    }) {
                        SpanText(resultWordLibrary.retry_submission.localized)
                    }
                } else {
                    SpanText(resultWordLibrary.please_wait.localized)
                }
            }
        }
    }
    else { ErrorAnswerPage(ctx) }
}

@Composable
fun ErrorAnswerPage(ctx: PageContext) {
    Box(
        Modifier
            .backgroundColor(userPageColor)
            .border {
                width(1.px)
                color(userPageBorderStrongColor)
                style(org.jetbrains.compose.web.css.LineStyle.Solid)
            }
            .borderRadius(1.cssRem)
            .padding(2.cssRem)
            .then(containerShadowStyle.toModifier())
            .maxWidth(80.percent)
            .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier.gap(1.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                modifier = Modifier.fontSize(FontSize.XXLarge),
                text = resultWordLibrary.error_unfulfilled.localized
            )
            Button(
                modifier = Modifier,
                onClick = {
                    ctx.router.navigateTo("/assessment")
                }
            ) {
                SpanText(resultWordLibrary.go_back.localized)
            }
        }
    }
}

